import { dateRenderer, dateSorter, numericSorter, priceRenderer } from '@lib/generic.utils'
import { notAnyPriceCheck, notAnyValueCheck } from '@/ag-grid/utils/notAnyValue'
import { get } from 'lodash'

export function agStartDateCol(properties = {}) {
  return {
    headerName: 'START DATE',
    field: 'start_date',
    cellRenderer: params => dateRenderer(params.value),
    type: 'centerAligned',
    comparator: dateSorter,
    filter: 'agTextColumnFilter',
    floatingFilterComponentFramework: 'TextDateFilter',
    ...properties
  }
}

export function agEndDateCol(properties = {}) {
  return {
    headerName: 'END DATE',
    field: 'end_date',
    cellRenderer: params => {
      if (params.data.one_time_purchase) return 'One time purchase'
      return dateRenderer(params.value)
    },
    type: 'centerAligned',
    comparator: dateSorter,
    filter: 'agTextColumnFilter',
    floatingFilterComponentFramework: 'TextDateFilter',
    ...properties
  }
}

export function agStatusCol(properties = {}) {
  return {
    headerName: 'STATUS',
    field: 'status.name',
    type: 'centerAligned',
    minWidth: 85,
    width: 85,
    maxWidth: 85,
    resizable: false,
    cellClass: ['status-column'],
    cellRenderer: params =>
      `<div class="status-col-badge" data-status="${params.value}">${params.value.slice(0, 1)}</div>`,
    filter: 'agTextColumnFilter',
    floatingFilterComponentFramework: 'DropdownFilter',
    filterParams: {
      options: [
        { label: 'Accepted', value: 'A' },
        { label: 'Cancelled', value: 'C' },
        { label: 'Pending', value: 'P' }
      ],
      noOptionsSort: true
    },
    ...properties
  }
}

export function agPriceCol(headerName, field, properties = {}) {
  return {
    headerName,
    field,
    cellRenderer: notAnyPriceCheck,
    type: 'rightAligned',
    cellEditorFramework: 'PriceEditor',
    comparator: (a, b, nodeA, nodeB) => {
      return numericSorter(get(nodeA.data, field), get(nodeB.data, field))
    },
    maxWidth: 150,
    filter: 'CustomFilter',
    floatingFilterComponentFramework: 'PriceFilter',
    filterParams: {
      debounceMs: 800
    },
    ...properties
  }
}

export function agDetailCol(properties = {}) {
  return {
    hide: true,
    headerName: 'DETAIL',
    field: 'name_reference',
    type: ['textFilterColumn'],
    cellRenderer: notAnyValueCheck,
    minWidth: 85,
    width: 85,
    ...properties
  }
}

export function agDepartmentsCol(filterInProgressModule, properties = {}, filterProperties = {}) {
  return {
    headerName: 'DEPARTMENTS',
    field: 'departments',
    cellRenderer: params => {
      if (!params.value.length) return 'N/A'

      const depNames = params.value.map(dep => dep.name)
      return `(${params.value.length}) ${depNames.join(',')}`
    },
    tooltipValueGetter: params => {
      if (!params?.data) return
      if (!params.value.length) return 'N/A'

      const depNames = params.value.map(dep => dep.name)
      return `(${params.value.length}) ${depNames.join(',')}`
    },
    filter: 'CustomFilter',
    floatingFilterComponentFramework: 'DropdownFilter',
    filterParams: {
      filterType: 'list_ids',
      type: 'contains',
      multiple: true,
      optionsModule: 'predictions',
      optionsState: 'departments',
      filterInProgressModule,
      filterInProgressState: 'filterInProgress',
      optionsHandler: options => {
        const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1)
        return options.map(o => ({ label: capitalize(o.name), value: o.id }))
      },
      ...filterProperties
    },
    ...properties
  }
}

export function agSelectCol(filterProperties = {}, properties = {}) {
  return {
    headerName: 'SELECT',
    checkboxSelection: true,
    lockVisible: true,
    sortable: false,
    minWidth: 50,
    width: 50,
    maxWidth: 50,
    pinned: 'left',
    resizable: false,
    type: ['centerAligned'],
    suppressMenu: true,
    filter: 'agTextColumnFilter',
    floatingFilterComponentFramework: 'HeaderCheckbox',
    filterParams: {
      vuexModule: 'lv-subscriptions-edit-mode',
      vuexState: 'selectAll',
      vuexMutation: 'setSelectAll',
      ...filterProperties
    },

    ...properties
  }
}

export function agOrderPlacedCol(properties = {}) {
  return {
    headerName: 'Order Placed',
    field: 'order_placed',
    cellRenderer: params => {
      if (!params.data) return
      return params.data.order_placed ? 'Yes' : 'No'
    },
    filter: 'CustomFilter',
    floatingFilterComponentFramework: 'DropdownFilter',
    filterParams: {
      filterType: 'boolean',
      type: 'contains',
      options: [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' }
      ]
    },
    headerClass: ['multiple-lines', 'text-center'],
    type: ['centerAligned'],
    maxWidth: 90,
    hide: true,
    ...properties
  }
}

export function agInvoiceReceivedCol(properties = {}) {
  return {
    headerName: 'Invoice received',
    field: 'invoice_received',
    cellRenderer: params => {
      if (!params.data) return
      return params.data.invoice_received ? 'Yes' : 'No'
    },
    filter: 'CustomFilter',
    floatingFilterComponentFramework: 'DropdownFilter',
    filterParams: {
      filterType: 'boolean',
      type: 'contains',
      options: [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' }
      ]
    },
    headerClass: ['multiple-lines', 'text-center'],
    type: ['centerAligned'],
    maxWidth: 90,
    hide: true,
    ...properties
  }
}

export function agInvoicePaidCol(properties = {}) {
  return {
    headerName: 'Invoice paid',
    field: 'invoice_paid',
    cellRenderer: params => {
      if (!params.data) return
      return params.data.invoice_received ? 'Yes' : 'No'
    },
    filter: 'CustomFilter',
    floatingFilterComponentFramework: 'DropdownFilter',
    filterParams: {
      filterType: 'boolean',
      type: 'contains',
      options: [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' }
      ]
    },
    headerClass: ['multiple-lines', 'text-center'],
    type: ['centerAligned'],
    maxWidth: 90,
    hide: true,
    ...properties
  }
}

export function checkboxCol(properties = {}) {
  return {
    width: 30,
    maxWidth: 30,
    checkboxSelection: true,
    headerCheckboxSelection: true,
    resizable: false,
    ...properties
  }
}

export function licenseCol(properties = {}) {
  return {
    headerName: 'License',
    field: 'license',
    cellRendererFramework: 'LinkRenderer',
    cellRendererParams: params => {
      return {
        route: `/licenses/${params.value[0]?.id}`,
        value: params.value[0]?.name
      }
    },
    filter: 'CustomFilter',
    floatingFilterComponentFramework: 'DropdownFilter',
    filterParams: {
      filterType: 'boolean',
      type: 'contains',
      options: [
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' }
      ]
    },
    type: ['centerAligned'],
    maxWidth: 90,
    hide: true,
    ...properties
  }
}

export function exchangeRateCol(properties = {}) {
  return agPriceCol('Exchange rate', 'exchange_rate', {
    editable: true,
    cellClass: ['editable-cell', 'ag-right-aligned-cell'],
    cellEditorParams: {
      decimals: 8
    },
    cellRenderer: params => priceRenderer(params.data.exchange_rate, 4) || 'N/A',
    tooltipValueGetter: params => priceRenderer(params.value, 8) || 'N/A',
    ...properties
  })
}

export const priceChangeCol = properties => ({
  headerName: 'Price change',
  field: 'percentage_price_change',
  type: 'rightAligned',
  hide: true,
  cellRenderer: params => (params.value ? `${params.value}%` : 'N/A'),
  filter: 'CustomFilter',
  floatingFilterComponentFramework: 'PriceFilter',
  filterParams: {
    debounceMs: 800
  },
  ...properties
})
